<template>
  <div
    class="relative flex h-full flex-col rounded-xl bg-white dark:bg-gray-800 bg-clip-border p-4 text-gray-700 dark:text-gray-200 shadow-xl shadow-blue-gray-900/5 transition-all duration-300 ease-in-out"
    :class="{
      'w-fit overflow-hidden': isCollapsed,
      'max-w-[20rem] landscape:max-w-[18rem] w-full overflow-x-hidden':
        !isCollapsed,
    }"
  >
    <!-- Collapse Button -->
    <button
      @click="isCollapsed = !isCollapsed"
      :class="{
        'relative right-0 h-8 w-8': isCollapsed,
        'absolute -right-3 h-11 w-11': !isCollapsed,
      }"
      class="hidden landscape:block top-2 z-20 rounded-full shadow hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-300 border border-gray-200 dark:border-gray-600 hover:scale-105 bg-gradient-to-l from-gray-200 to-gray-500 dark:from-gray-800 dark:to-gray-700"
      :title="isCollapsed ? 'Expand Menu' : 'Collapse Menu'"
    >
      <i
        :class="
          isCollapsed ? 'fa-solid fa-chevron-right' : 'fa-solid fa-chevron-left'
        "
        class="text-gray-700 dark:text-gray-200 text-sm transition-transform duration-300"
      ></i>
    </button>

    <!-- Logo Section -->
    <div
      v-if="!isCollapsed"
      class="flex items-center gap-4 p-4 mb-2 border-b-[1px] border-gray-200 dark:border-gray-700 transition-opacity duration-300"
      :class="{ 'opacity-0': isCollapsed }"
    >
      <img
        src="/logo1.png"
        alt="brand"
        class="h-8 dark:invert dark:brightness-0 transition-all duration-300 hover:scale-105"
      />
    </div>

    <!-- Navigation Links -->
    <nav
      v-if="!isCollapsed"
      class="flex min-w-[240px] flex-col gap-1 p-2 text-base font-normal text-main-700 dark:text-gray-300"
    >
      <!-- Store List -->
      <div class="relative block w-full" v-if="!isCollapsed">
        <StoreListComponent @closeSidebar="closePopOnMain" />
      </div>

      <!-- Dashboard -->
      <router-link
        to="/dashboard"
        exact-active-class="bg-main-50 dark:bg-gray-700 rounded-lg"
        class="relative block w-full"
      >
        <DashboardNavComponent />
      </router-link>

      <!-- Orders -->
      <div class="relative block w-full">
        <OrdersComponent />
      </div>

      <!-- Products -->
      <div class="relative block w-full">
        <ProductComponent />
      </div>

      <!-- Analytics -->
      <router-link
        to="/analytics"
        active-class="bg-main-50 dark:bg-gray-700 rounded-lg"
        class="relative block w-full"
        v-if="!isCollapsed"
      >
        <div
          role="button"
          class="flex items-center w-full p-0 leading-tight transition-all rounded-lg outline-none text-start hover:bg-main-50 dark:hover:bg-gray-700 hover:text-main-900 dark:hover:text-gray-100 focus:bg-main-50 dark:focus:bg-gray-700 focus:text-main-900 dark:focus:text-gray-100 active:bg-main-50 dark:active:bg-gray-700 active:text-main-900 dark:active:text-gray-100"
        >
          <button
            type="button"
            class="flex items-center justify-between w-full p-3 text-xl antialiased font-semibold leading-snug text-left transition-colors border-b-0 select-none border-b-main-100 text-main-700 dark:text-gray-300 hover:text-main-900 dark:hover:text-gray-100"
          >
            <div class="grid w-5 relative mr-4 place-items-center">
              <i class="fa-solid fa-chart-simple text-[16px]"></i>
            </div>
            <p
              class="block mr-auto text-base antialiased font-normal leading-relaxed text-main-900 dark:text-gray-200"
            >
              Analytics
            </p>
            <i
              class="fa-solid fa-chevron-right text-[12px] ml-4 transition-transform duration-300"
            ></i>
          </button>
        </div>
      </router-link>

      <!-- Promotion -->
      <router-link
        to="/promotion"
        active-class="bg-main-50 dark:bg-gray-700 rounded-lg"
        class="relative block w-full"
        v-if="isAdmin && !isCollapsed"
      >
        <div
          role="button"
          class="flex items-center w-full p-0 leading-tight transition-all rounded-lg outline-none text-start hover:bg-main-50 dark:hover:bg-gray-700 hover:text-main-900 dark:hover:text-gray-100 focus:bg-main-50 dark:focus:bg-gray-700 focus:text-main-900 dark:focus:text-gray-100 active:bg-main-50 dark:active:bg-gray-700 active:text-main-900 dark:active:text-gray-100"
        >
          <button
            type="button"
            class="flex items-center justify-between w-full p-3 text-xl antialiased font-semibold leading-snug text-left transition-colors border-b-0 select-none border-b-main-100 text-main-700 dark:text-gray-300 hover:text-main-900 dark:hover:text-gray-100"
          >
            <div class="grid w-5 relative mr-4 place-items-center">
              <i class="fa-solid fa-rectangle-ad text-[16px]"></i>
            </div>
            <p
              class="block mr-auto text-base antialiased font-normal leading-relaxed text-main-900 dark:text-gray-200"
            >
              Advertisement
            </p>
            <i
              class="fa-solid fa-chevron-right text-[12px] ml-4 transition-transform duration-300"
            ></i>
          </button>
        </div>
      </router-link>

      <!-- Divider -->
      <hr
        class="my-2 border-main-50 dark:border-gray-700"
        v-if="!isCollapsed"
      />

      <!-- Notifications -->
      <NotificationNavComponent />

      <!-- Profile -->
      <ProfileNavComponent v-if="isAdmin" />

      <!-- Settings -->
      <SettingsComponent v-if="isAdmin" />

      <!-- Log Out -->
      <div
        role="button"
        @click="handleLogout"
        class="flex items-center w-full p-3 leading-tight transition-all rounded-lg outline-none text-start hover:bg-main-50 dark:hover:bg-gray-700 hover:text-main-900 dark:hover:text-gray-100 focus:bg-main-50 dark:focus:bg-gray-700 focus:text-main-900 dark:focus:text-gray-100 active:bg-main-50 dark:active:bg-gray-700 active:text-main-900 dark:active:text-gray-100"
      >
        <div class="grid mr-4 place-items-center">
          <i class="fa-solid fa-power-off text-xl"></i>
        </div>
        <span v-if="!isCollapsed" class="transition-opacity duration-300"
          >Log Out</span
        >
      </div>
    </nav>

    <!-- Alert Component -->
    <template v-if="!isCollapsed"><NavAlertComponent /></template>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import StoreListComponent from "./storeListComponent.vue";
import OrdersComponent from "./ordersComponent.vue";
import ProductComponent from "./productComponent.vue";
import SettingsComponent from "./settingsComponent.vue";
import NavAlertComponent from "./navAlertComponent.vue";
import DashboardNavComponent from "./dashboardComponent.vue";
import NotificationNavComponent from "./notificationNavComponent.vue";
import ProfileNavComponent from "./profileNavComponent.vue";

export default {
  components: {
    StoreListComponent,
    OrdersComponent,
    ProductComponent,
    SettingsComponent,
    NavAlertComponent,
    DashboardNavComponent,
    NotificationNavComponent,
    ProfileNavComponent,
  },
  name: "NavigationLayout",
  data() {
    return {
      isCollapsed: false,
      isAdmin: this.$store.getters.isAdmin,
    };
  },
  watch: {
    "$store.getters.isAdmin"(newVal) {
      this.isAdmin = newVal;
    },
  },
  methods: {
    ...mapActions(["logout"]),
    async handleLogout() {
      try {
        await this.logout();
        if (!this.$store.getters.isAuthenticated) {
          this.$router.push("/login");
        }
      } catch (error) {
        console.error("Logout failed:", error);
      }
    },
    closePopOnMain() {
      this.$emit("closePop");
    },
  },
};
</script>

<style>
/* Custom scrollbar that only shows when needed */
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 #ffffff;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #ffffff;
  border-radius: 3px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: #e2e8f0;
  border-radius: 3px;
}

.dark .scrollbar-thin {
  scrollbar-color: #4b5563 #1f2937;
}

.dark .scrollbar-thin::-webkit-scrollbar-track {
  background: #1f2937;
}

.dark .scrollbar-thin::-webkit-scrollbar-thumb {
  background-color: #4b5563;
}

/* Smooth transitions for collapsed state */
.nav-item-collapsed {
  @apply justify-center p-3;
}

.nav-item-collapsed span {
  @apply hidden;
}

.nav-item-collapsed i {
  @apply mx-auto;
}
</style>
